import * as L from './loadables';

/*eslint no-unexpected-multiline: 0*/
import React, { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { withRouter } from 'react-router';
import { Helmet } from 'react-helmet';
import {
  getUrlParams,
  pathContains,
  storeEnrollmentLocalStorage,
} from './utils';
import FourOhFour from './routes/404';
import LoadingIndicator from './components/loadingIndicator';
import MainLayout from './components/mainLayout';
import ProgramErrorBoundary from './routes/program/errorBoundary';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { actions } from './actions';
import api from './services';
import { connect } from 'react-redux';
import detector from 'detector';

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: ``,
    };
    this.setup();
  }

  setup = async () => {
    this.props.clearErrors();
    window.onpopstate = this.onBackButtonEvent;

    const storage = api.localStorage.get();

    if (storage) {
      this.props.establishUser(storage);
    }

    const { pathname } = this.props.location;
    if (this.isSurvey(pathname)) {
      const params = getUrlParams(window.location.search);
      const node_slug = pathname.split('/')[2];
      const url_params = this.generateGetRequestQueryParams(
        _.omit(params, 'auth_token'),
      );

      api.cookie.set('auth_token', params['auth_token']);
      api.cookie.set('enrollment_identifier', params['enrollment_identifier']);
      api.cookie.set('occurrence', params['occurrence']);

      await this.props.getMeta();
      await this.props.getNode(this.props.meta, this.generateGetRequestUrl());
      await this.props.getSurvey(node_slug, params['auth_token'], url_params);

      this.setState({ loading: false });
    } else if (!storage || !storage.enrollment_identifier) {
      await this.props.getMeta();
      await this.props.getNode(this.props.meta, this.generateGetRequestUrl());

      this.setState({ loading: false });
    } else {
      const { payload } = await this.props.getUser(
        storage.enrollment_identifier,
        storage.participant_auth_token,
      );
      this.setEnrollmentValues(payload);

      await this.props.getMeta();
      this.props.getNode(this.props.meta, this.generateGetRequestUrl());
    }
  };

  isSurvey = pathname => {
    return pathname.split('/')[1] === 'surveys';
  };

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.participant.enrollment_identifier &&
      this.differentNodes(nextProps)
    ) {
      this.evaluateRoute(nextProps);
    }
  }

  differentNodes = nextProps => {
    return nextProps.participant.nodes !== this.props.participant.nodes;
  };

  onBackButtonEvent = e => {
    e.preventDefault();
    this.getAllData();
  };

  // sends query params to triage on landing page
  generateGetRequestUrl = () => {
    const url_params = getUrlParams(window.location.search);
    delete url_params['preview_token'];

    if (!_.isEmpty(url_params)) {
      let params_string = this.generateGetRequestQueryParams(url_params);
      return `node/?${params_string}`;
    } else {
      return `node/`;
    }
  };

  generateGetRequestQueryParams = url_params => {
    return _.map(url_params, (value, field) =>
      field && value ? `${field}=${value}&` : null,
    ).join('');
  };

  getAllData = async () => {
    if (!this.props.participant.enrollment_identifier) {
      const storage = api.localStorage.get();

      if (storage) {
        this.props.establishUser(storage);
      }
    }

    if (!this.props.participant.enrollment_identifier) {
      await this.props.getMeta();
      await this.props.getNode(this.props.meta, this.generateGetRequestUrl());
      this.setState({ loading: false });
    } else {
      const { payload: user } = await this.props.getUser(
        this.props.participant.enrollment_identifier,
        this.props.participant.participant_auth_token,
      );
      this.setEnrollmentValues(user);
      await this.props.getMeta();
      return this.props.getNode(this.props.meta, this.generateGetRequestUrl());
    }
  };

  setEnrollmentValues = user => {
    this.setState({ loading: false });
    return storeEnrollmentLocalStorage(user);
  };

  submitUserData = (user_data, transition = true) => {
    this.props.clearErrors();
    this.setState({ loading: transition });

    const url_params = getUrlParams(window.location.search);

    const {
      enrollment_identifier = ``,
      participant_auth_token = ``,
    } = this.props.participant;

    const payload = { ...user_data, ...url_params, client_info: detector };

    return this.props
      .updateUser(payload, enrollment_identifier, participant_auth_token)
      .then(({ payload }) => this.setEnrollmentValues(payload));
  };

  findNodes(props) {
    switch (true) {
      case _.has(props, 'participant') && _.has(props.participant, 'nodes'):
        return props.participant;
      case _.has(this.props.node, 'nodes'):
        return this.props.node;
      default:
        return { nodes: [], layout: '' };
    }
  }

  evaluateRoute = (newProps = this.props) => {
    const data = this.findNodes(newProps);

    const { history } = this.props;
    const currentPath = history.location.pathname;
    const { nodes } = data;
    const { layout } = data;
    const currentLayout = layout === `education` ? `` : layout;
    this.setState({ error: '' });
    if (nodes.length <= 0) {
      // if there are no nodes
      console.error('No nodes present');
      return history.replace(`/404`);
    }

    if (
      // need to stop the current layout from navigating away from settings/summary
      // || currentPath === `/` <- this allows us to have routing to education, but we dont have the data yet
      currentLayout === `dashboard` &&
      pathContains(currentPath, [`faq`, `settings`, `summary`, `dashboard`])
    ) {
      return false;
    } else if (
      // special case for Education layout
      // using layout here because we switch currentLayout to a blank string
      layout === `education` &&
      pathContains(currentPath, [`log-in`])
    ) {
      return false;
    } else if (pathContains(currentPath, [`terms`, `one_click_contribution`])) {
      // allow access to terms at any time
      return false;
    } else if (_.includes(currentLayout, currentPath)) {
      return false;
    } else if (layout === `follow_up`) return false;

    // console.info(`Transitioning to: ${currentLayout}${window.location.search}`);
    history.replace(
      `/${currentLayout}${window.location.search}${window.location.hash}`,
    );
  };

  mergeProps = (props, ...etc) => {
    const { error } = this.state;

    return {
      ...props,
      ...{ ...etc },
      evaluateRoute: this.evaluateRoute,
      mergeProps: this.mergeProps,
      submitUserData: this.submitUserData,
      getAllData: this.getAllData,
      error,
      ...this.props,
      match: props.match,
    };
  };

  commonRender = (Module, props) => {
    const { nodes } = this.props.participant.nodes
      ? this.props.participant
      : this.props.node;

    if (!nodes || nodes.length === 0) {
      return <FourOhFour />;
    }

    return Module && nodes.length >= 1 ? (
      <Module {...this.mergeProps(props)} />
    ) : (
      <LoadingIndicator />
    );
  };

  render() {
    const { meta } = this.props;
    const { loading } = this.state;

    if (meta.is_program) {
      return (
        <ProgramErrorBoundary>
          <L.Program {...this.props} />
        </ProgramErrorBoundary>
      );
    }

    return (
      <MainLayout loading={loading}>
        <Helmet>
          <meta content={meta.keywords} name="keywords" />
          <meta content="A study run by Evidation Health" name="description" />
          <meta content="Evidation Health" name="author" />
          <meta content="help@myachievement.com" name="contact" />
        </Helmet>

        <Route
          exact
          path="/"
          render={props => this.commonRender(L.Education, props)}
        />
        <Route
          exact
          path="/surveys/:slug"
          render={props => this.commonRender(L.FollowUp, props)}
        />
        <Route
          exact
          path="/log-in"
          render={props => this.commonRender(L.LogIn, props)}
        />
        <Route
          path="/screener"
          render={props => this.commonRender(L.Enrollment, props)}
        />
        <Route
          path="/verify_email"
          render={props => this.commonRender(L.VerifyEmail, props)}
        />
        <Route
          path="/multi_factor_authenticate"
          render={props => this.commonRender(L.MultifactorAuth, props)}
        />
        <Route
          path="/visit_portal"
          render={props => this.commonRender(L.GenericNonDashboard, props)}
        />
        <Route
          path="/event_delay"
          render={props => this.commonRender(L.GenericNonDashboard, props)}
        />
        <Route
          path="/registration"
          render={props => this.commonRender(L.Enrollment, props)}
        />
        <Route
          path="/informed_consent"
          render={props => this.commonRender(L.Enrollment, props)}
        />
        <Route
          path="/file_upload"
          render={props => this.commonRender(L.Enrollment, props)}
        />
        <Route
          exact
          path="/manage/dashboard"
          initalEntries={[{ key: 'informed_consent' }]}
          render={props => this.commonRender(L.Dashboard, props)}
        />
        <Route
          exact
          path="/manage/dashboard/contribution/:slug"
          render={props =>
            this.commonRender(L.Contribution, {
              ...props,
              getAllData: this.getAllData,
            })
          }
        />
        <Route
          exact
          path="/manage/dashboard/diary/:node_slug"
          render={props =>
            this.commonRender(L.Diary, {
              getAllData: this.getAllData,
              ...props,
            })
          }
        />
        <Route
          exact
          path="/manage/dashboard/diary/:node_slug/complete"
          render={props => this.commonRender(L.DiaryComplete, props)}
        />
        <Route
          path="/manage/settings"
          render={props => this.commonRender(L.Settings, props)}
        />
        <Route
          path="/manage/summary"
          render={props => this.commonRender(L.Summary, props)}
        />
        <Route path="/faq" render={props => this.commonRender(L.Faq, props)} />
        <Route
          path="/terminal_state"
          render={props => this.commonRender(L.TerminalState, props)}
        />
        <Route
          path="/withdrawn"
          render={props => this.commonRender(L.TerminalState, props)}
        />

        <Switch>
          <Route
            path="/one_click_contribution/error"
            render={props => this.commonRender(L.OneClickError, props)}
          />

          <Route
            path="/one_click_contribution"
            render={props => this.commonRender(L.OneClick, props)}
          />
        </Switch>
        <Route path="/login" exact render={() => <Redirect to={`/log-in`} />} />
        <Route
          path="/dashboard"
          exact
          render={() => <Redirect to={`/manage/dashboard`} />}
        />
        <Route
          path="/dashboard/contribution/:slug"
          exact
          render={() => (
            <Redirect to={`/manage/dashboard/contribution/:slug`} />
          )}
        />
        <Route
          path="/settings"
          exact
          render={() => <Redirect to={`/manage/dashboard`} />}
        />
        <Route
          path="/summary"
          exact
          render={() => <Redirect to={`/manage/summary`} />}
        />
        <Route
          path="/unauthorized"
          render={props => this.commonRender(L.Unauthorized, props)}
        />
        <Route
          path="/settings"
          exact
          render={() => <Redirect to={`/manage/dashboard`} />}
        />
        <Route
          path="/summary"
          exact
          render={() => <Redirect to={`/manage/summary`} />}
        />
        <Route
          path="/unauthorized"
          render={props => this.commonRender(L.Unauthorized, props)}
        />
        <Route path="/404" component={FourOhFour} />
      </MainLayout>
    );
  }
}

App.propTypes = {
  participant: PropTypes.object,
  history: PropTypes.object.isRequired,
};

export default withRouter(
  connect(
    ({ participant, meta, node }) => ({
      participant,
      meta,
      node,
    }),
    dispatch => ({
      logoutUser: user => dispatch(actions.logoutUser(user)),
      establishUser: user => dispatch(actions.establishUser(user)),
      updateUser: (user_data, enrollment_id, auth_token) =>
        dispatch(actions.updateUser(user_data, enrollment_id, auth_token)),
      loginUser: (user_data, enrollment_id, auth_token) =>
        dispatch(actions.loginUser(user_data, enrollment_id, auth_token)),
      getUser: (enrollment_id, auth_token) =>
        dispatch(actions.getUser(enrollment_id, auth_token)),
      createUser: user => dispatch(actions.createUser(user)),
      getMeta: data => dispatch(actions.getMeta()),
      getSurvey: (slug, auth_token, queryString) =>
        dispatch(actions.getSurvey(slug, auth_token, queryString)),
      getNode: (data, request) => dispatch(actions.getNode(data, request)),
      clearErrors: () => dispatch(actions.clearErrors()),
    }),
  )(App),
);



// WEBPACK FOOTER //
// ./src/app.js